import {
  Buildings,
  ChartBar,
  ChatText,
  CheckCircle,
  ClipboardText,
  CreditCard,
  CurrencyDollar,
  Garage,
  Globe,
  NavigationArrow,
  PlugsConnected,
  Receipt,
  ShieldCheck,
  SteeringWheel,
  Truck,
  Users,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import {
  EVERY_PLAN,
  EVERYONE,
  holdsAnyOf,
  type CompanyFeature,
  type OfficeRole,
  type PagePermissions,
  type PagePlan,
  type PermissionKey,
} from '@relay-towing/api';

export type TabId =
  | 'home'
  | 'drivers'
  | 'calls'
  | 'impounds'
  | 'invoices'
  | 'inspections'
  | 'scanner'
  | 'analytics';

/**
 * The old app's tabs: two fixed sets, as production has shown them since
 * 2026-10-09. Working them out from the Roles page checkboxes gave an
 * office member who drives both sets, so a new owner saw eight tabs where
 * they had six, and the app changed under every phone on a store build
 * (hotfix b2659fb0, undone by a merge and put back here). They stay fixed
 * until the old app is deleted with the builds that load it; the new app's
 * tabs are mobileTabsFor's. What a role may do inside a tab is the server's.
 */
const OFFICE_TABS: readonly TabId[] = [
  'home',
  'drivers',
  'impounds',
  'invoices',
  'inspections',
  'analytics',
];

const FIELD_TABS: readonly TabId[] = [
  'calls',
  'impounds',
  'invoices',
  'inspections',
  'scanner',
];

/**
 * The office set for any office role, whether or not they drive, and the
 * field set for someone who only drives.
 */
export function tabsFor({
  officeRole,
  drives,
}: {
  officeRole: OfficeRole | null;
  drives: boolean;
}): TabId[] {
  if (officeRole) return [...OFFICE_TABS];
  return drives ? [...FIELD_TABS] : [];
}

/** The tab to open: the one asked for if it is shown, else the first. */
export function initialTab(tabs: readonly TabId[], preferred?: string): TabId {
  const asked = tabs.find((tab) => tab === preferred);
  return asked ?? tabs[0] ?? 'calls';
}

// ─── The new mobile shell (src/mobile) ──────────────────────────────────────

/**
 * Who is asking: their office role, whether they drive, their checkboxes,
 * and the optional features their company has turned on.
 */
export interface Member {
  officeRole: OfficeRole | null;
  drives: boolean;
  permissions: readonly PermissionKey[];
  features: readonly CompanyFeature[];
}

export type FunctionId =
  | 'impounds'
  | 'dispatch-chat'
  | 'invoices'
  | 'inspections';

export type OperationId =
  | 'drivers'
  | 'reports'
  | 'team'
  | 'roles'
  | 'trucks'
  | 'rates'
  | 'billing'
  | 'company'
  | 'integrations'
  | 'website';

/**
 * Where an item opens: a screen of this app by its path, or a page of the
 * dashboard by its route there, opened in the phone's browser
 * (`links.dashboardPage`). The dashboard is where those are run; the app
 * does not rebuild them.
 */
export type Destination = { screen: string } | { dashboard: string };

export interface TabItem<Id extends string = string> {
  id: Id;
  label: string;
  icon: PhosphorIcon;
  /** Any one of these shows it. Every item here needs one. */
  permissions: readonly PermissionKey[];
  /**
   * The plan feature it needs. It still shows on a plan without it, as
   * the dashboard's links do: the page it opens offers the upgrade.
   */
  plan: PagePlan;
  /** Only for an office role, whatever a driver holds. */
  officeOnly?: true;
  /** Only for these office roles, whatever else they hold. */
  roles?: readonly OfficeRole[];
  /**
   * A screen of its own, not a list: it shows no tab bar, the Functions tab
   * never opens on it, and going to it is a step back can return from.
   */
  fullScreen?: true;
  opens: Destination;
}

/**
 * The Functions tab: the company's records the member works in, each by the
 * key that lets them see it. The lists are scoped by the server, so the
 * same item shows a driver their own invoices and the office the company's.
 */
export const FUNCTION_ITEMS: readonly TabItem<FunctionId>[] = [
  {
    id: 'impounds',
    label: 'Impounds',
    icon: Garage,
    permissions: ['impounds.view_own', 'impounds.view_all'],
    plan: EVERY_PLAN,
    opens: { screen: '/impounds' },
  },
  // Paste a call and the chat fills it in; it only creates calls. Owners
  // only for now (James, 2026-11-04): not dispatchers, not drivers.
  {
    id: 'dispatch-chat',
    label: 'Dispatch Chat',
    icon: ChatText,
    permissions: ['calls.create'],
    roles: ['OWNER'],
    plan: EVERY_PLAN,
    fullScreen: true,
    opens: { screen: '/dispatch-chat' },
  },
  {
    id: 'invoices',
    label: 'Invoices',
    icon: Receipt,
    permissions: ['invoices.view_own', 'invoices.view_all'],
    plan: EVERY_PLAN,
    opens: { screen: '/invoices' },
  },
  {
    // Their own inspections need no view: running one is enough.
    id: 'inspections',
    label: 'Inspections',
    icon: ClipboardText,
    permissions: ['inspections.view', 'inspections.create'],
    plan: EVERY_PLAN,
    opens: { screen: '/inspections' },
  },
];

/**
 * The Operations tab: running the business. Drivers opens its screen here;
 * the rest open the dashboard page that does it, gated by the key and plan
 * the dashboard gates it by (dashboardNav.ts, settingsTabs.ts). Reports is
 * the dashboard's page: the app has no analytics of its own, by the
 * owner's decision of 2026-10-19. Team members and roles go together
 * under the keys for managing people, there and here.
 */
export const OPERATION_ITEMS: readonly TabItem<OperationId>[] = [
  {
    // The map of everyone's calls and trucks.
    id: 'drivers',
    label: 'Drivers',
    icon: SteeringWheel,
    permissions: ['calls.view_all'],
    plan: EVERY_PLAN,
    officeOnly: true,
    opens: { screen: '/drivers' },
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: ChartBar,
    permissions: ['reports.view'],
    plan: EVERY_PLAN,
    opens: { dashboard: '/reports' },
  },
  {
    id: 'team',
    label: 'Team members',
    icon: Users,
    permissions: ['users.add', 'users.edit', 'users.drop'],
    plan: EVERY_PLAN,
    opens: { dashboard: '/settings?tab=accounts' },
  },
  {
    id: 'roles',
    label: 'Roles',
    icon: ShieldCheck,
    permissions: ['users.add', 'users.edit', 'users.drop'],
    plan: EVERY_PLAN,
    opens: { dashboard: '/settings?tab=roles' },
  },
  {
    id: 'trucks',
    label: 'Trucks',
    icon: Truck,
    permissions: ['trucks.edit'],
    plan: EVERY_PLAN,
    opens: { dashboard: '/settings?tab=trucks' },
  },
  {
    id: 'rates',
    label: 'Rates',
    icon: CurrencyDollar,
    permissions: ['rates.view'],
    plan: EVERY_PLAN,
    opens: { dashboard: '/customer-rates' },
  },
  {
    id: 'billing',
    label: 'Plan and billing',
    icon: CreditCard,
    permissions: ['subscription.view'],
    plan: EVERY_PLAN,
    opens: { dashboard: '/settings?tab=billing' },
  },
  {
    id: 'company',
    label: 'Company settings',
    icon: Buildings,
    permissions: ['company.edit'],
    plan: EVERY_PLAN,
    opens: { dashboard: '/settings?tab=application' },
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: PlugsConnected,
    permissions: ['integrations.manage'],
    plan: EVERY_PLAN,
    opens: { dashboard: '/integrations' },
  },
  {
    id: 'website',
    label: 'Website',
    icon: Globe,
    permissions: ['website.edit'],
    plan: 'website',
    opens: { dashboard: '/website' },
  },
];

const shows = (item: TabItem, m: Member): boolean =>
  (item.officeOnly !== true || m.officeRole !== null) &&
  (!item.roles ||
    (m.officeRole !== null && item.roles.includes(m.officeRole))) &&
  item.permissions.some((key) => m.permissions.includes(key));

/** The Functions items this member's role allows, in the tab's order. */
export const functionsFor = (member: Member): TabItem<FunctionId>[] =>
  FUNCTION_ITEMS.filter((item) => shows(item, member));

/** The Operations items this member's role allows, in the tab's order. */
export const operationsFor = (member: Member): TabItem<OperationId>[] =>
  OPERATION_ITEMS.filter((item) => shows(item, member));

export type MobileTabId =
  | 'home'
  | 'calls'
  | FunctionId
  | 'functions'
  | 'operations';

/** Whether the member has this Functions item, by its keys. */
const hasFunction = (m: Member, id: FunctionId): boolean =>
  functionsFor(m).some((item) => item.id === id);

/**
 * The new shell's tabs. Everyone has Home, which is their own day or the
 * company's. A driver always has their own calls; an office member who
 * does not drive sees Calls only with the company's (`calls.view_all`),
 * since they are on none themselves.
 *
 * An office role, driving or not, has Functions, holding the
 * records they work in. Someone who only drives has few of those and opens
 * them all day, so each is a tab of its own instead, by the same keys as
 * its Functions item (decided 2026-10-19). Functions and Operations show
 * when they have an item, so someone who only drives normally has no
 * Operations: an empty tab would be a dead end.
 */
const MOBILE_TABS: ReadonlyArray<
  readonly [MobileTabId, (m: Member) => boolean]
> = [
  ['home', () => true],
  ['calls', (m) => m.drives || m.permissions.includes('calls.view_all')],
  ['invoices', (m) => m.officeRole === null && hasFunction(m, 'invoices')],
  ['impounds', (m) => m.officeRole === null && hasFunction(m, 'impounds')],
  [
    'inspections',
    (m) => m.officeRole === null && hasFunction(m, 'inspections'),
  ],
  ['functions', (m) => m.officeRole !== null && functionsFor(m).length > 0],
  ['operations', (m) => operationsFor(m).length > 0],
];

export function mobileTabsFor(member: Member): MobileTabId[] {
  return MOBILE_TABS.filter(([, shows]) => shows(member)).map(([id]) => id);
}

export type CallView = 'active' | 'assigned' | 'completed';

export interface CallViewItem {
  id: CallView;
  label: string;
  icon: PhosphorIcon;
  /** Any one of these shows it. */
  permissions: PagePermissions;
  /** Left out for a member holding this: they have the view that replaces it. */
  replacedBy?: PermissionKey;
  plan: PagePlan;
  /** What the server lists in it, for screens.md; the query is callLists.ts. */
  lists: string;
  /** What it says with nothing in it. */
  empty: string;
}

/**
 * The calls list's switcher, the Figma's icons before each: an arrow for
 * under way, a tick for done. With the company's calls it is Active and
 * Completed; with only your own it is Assigned and Completed, which is what
 * those lists are. The server scopes every one.
 */
export const CALL_VIEWS: readonly CallViewItem[] = [
  {
    id: 'active',
    label: 'Active',
    icon: NavigationArrow,
    permissions: ['calls.view_all'],
    plan: EVERY_PLAN,
    lists: "the company's open calls",
    empty: 'No active calls',
  },
  // Every member has the calls assigned to them, with no checkbox.
  {
    id: 'assigned',
    label: 'Assigned',
    icon: NavigationArrow,
    permissions: EVERYONE,
    replacedBy: 'calls.view_all',
    plan: EVERY_PLAN,
    lists:
      'their own open calls, and outside calls they took while the motor club confirms',
    empty: 'No calls assigned to you',
  },
  // The server scopes it: their own, or the company's with the view.
  {
    id: 'completed',
    label: 'Completed',
    icon: CheckCircle,
    permissions: EVERYONE,
    plan: EVERY_PLAN,
    lists: 'the completed calls they may see',
    empty: 'No completed calls',
  },
];

export type CallSection = 'offers' | 'unassigned';

export interface CallSectionItem {
  id: CallSection;
  label: string;
  /** Any one of these shows it. */
  permissions: PagePermissions;
  plan: PagePlan;
  /** The views it heads. */
  heads: readonly CallView[];
  /** What the server lists in it, for screens.md; the query is callLists.ts. */
  lists: string;
}

/**
 * Calls that are not the member's but that they may see, each as its own
 * section at the top of a view's calls while it has any, until there is a
 * design for them. Outside offers had a view of their own, which hid them
 * one tap away from the calls a driver watches.
 *
 * Unassigned heads only Assigned: Active is every open call, those with no
 * driver among them. "View unassigned calls" was offered to the Driver set
 * again on 2026-10-19, for the existing companies' drivers who held it, and
 * the new app had nowhere to show them. The server keeps outside offers out
 * of it: they are OFFERED, which no other view lists.
 */
export const CALL_SECTIONS: readonly CallSectionItem[] = [
  {
    id: 'offers',
    label: 'Outside offers',
    permissions: ['calls.accept_outside'],
    plan: EVERY_PLAN,
    heads: ['active', 'assigned'],
    lists: 'calls offered to the company from outside it that no driver has',
  },
  {
    id: 'unassigned',
    label: 'Unassigned',
    permissions: ['calls.view_unassigned'],
    plan: EVERY_PLAN,
    heads: ['assigned'],
    lists: 'open calls with no driver yet',
  },
];

const holds = (member: Member) => (key: PermissionKey) =>
  member.permissions.includes(key);

/** The calls views this member has, in the switcher's order. */
export const callViewsFor = (member: Member): CallViewItem[] =>
  CALL_VIEWS.filter(
    (view) =>
      holdsAnyOf(view.permissions, holds(member)) &&
      !(view.replacedBy && holds(member)(view.replacedBy))
  );

/** Whether a section heads this view's calls for this member. */
export const headsView = (
  section: CallSectionItem,
  view: CallViewItem,
  member: Member
): boolean =>
  section.heads.includes(view.id) &&
  holdsAnyOf(section.permissions, holds(member));

/**
 * Whether Home is the office's (the company's money and work) or a driver's
 * own day. An office member who also drives gets the office's.
 */
export const seesOfficeHome = (member: Member): boolean =>
  member.officeRole !== null;

/**
 * Whether Home lists tasks: only while the company has Tasks turned on
 * (Settings → Features), which it is not until someone does. Then a driver
 * has their own without a checkbox, and `tasks.view_all` shows everyone's.
 * Tasks are on Home only; Functions had an item that did no more than
 * scroll to them.
 */
export const seesTasks = (member: Member): boolean =>
  member.features.includes('tasks') &&
  (member.drives || member.permissions.includes('tasks.view_all'));
